import { useQuery, useQueryClient } from 'react-query';

import { apiClient } from 'src/services/api/client';
import { queryKeys } from 'src/services/api/constants';
import { GET_LEADS_PATH } from 'src/services/api/leads/constants';
import type { SpeakerMessage } from 'src/services/lead-facts/types';

export type ExtractFactsRequest = {
    shouldCall: boolean;
    slice: SpeakerMessage[];
};

export type ExtractFactsResponse = {
    facts: Record<string, string | number | boolean | null>;
};

export const getExtractFactsQueryKey = (leadUuid: string) => [queryKeys.factSuggestions, leadUuid];

/**
 * Run the backend LLM extraction over the conversation slice (budget-enforced server-side);
 * invalidates the lead on success so the new `pending` values resurface as suggestions.
 */
export const useExtractFacts = (leadUuid: string, request: ExtractFactsRequest) => {
    const queryClient = useQueryClient();
    const sliceKey = request.slice.map((m) => `${m.direction}:${m.contents}`).join('|');

    return useQuery(
        [...getExtractFactsQueryKey(leadUuid), sliceKey],
        async () => {
            return apiClient(`${GET_LEADS_PATH}/${leadUuid}/extract-facts`, {
                method: 'post',
                json: { messages: request.slice },
            }).json<ExtractFactsResponse>();
        },
        {
            enabled: request.shouldCall,
            retry: false,
            staleTime: Infinity,
            onSuccess: () => {
                queryClient.invalidateQueries(queryKeys.leads);
            },
        }
    );
};
